import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ViewType } from './menuConfig';
import { Menu, User, LogOut, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBranch } from '../../contexts/BranchContext';

interface AppHeaderProps {
  onToggleSidebar: () => void;
  activeView: ViewType;
  onLogout: () => void;
  isSidebarCollapsed: boolean;
  onToggleSidebarCollapse: () => void;
}

export function AppHeader({ onToggleSidebar, activeView, onLogout, isSidebarCollapsed, onToggleSidebarCollapse }: AppHeaderProps) {
  const navigate = useNavigate();
  const { user, userBranches } = useAuth();
  const { selectedBranch, setSelectedBranch } = useBranch();

  // Initialize selectedBranch with user's defaultBranchId only if no branch is selected
  // (persisted branch from localStorage takes precedence)
  useEffect(() => {
    if (user?.defaultBranchId && selectedBranch === null) {
      setSelectedBranch(user.defaultBranchId);
    }
  }, [user?.defaultBranchId, selectedBranch, setSelectedBranch]);

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  return (
    <header className="flex-shrink-0 h-16 border-b border-primary-200/50 bg-gradient-to-r from-white/90 via-primary-50/30 to-white/90 backdrop-blur-sm px-4 flex items-center gap-4 shadow-sm min-h-[64px]">
      <Button
        variant="ghost"
        size="sm"
        onClick={onToggleSidebar}
        className="lg:hidden text-primary-600 hover:bg-primary-50 hover:text-primary-700"
      >
        <Menu className="h-5 w-5" />
      </Button>
      
      {/* Sidebar collapse/expand button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onToggleSidebarCollapse}
        className="hidden lg:flex text-primary-600 hover:bg-primary-50 hover:text-primary-700 h-8 w-8 p-0"
      >
        {isSidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
      </Button>
      
      {/* Welcome message with clickable username */}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 font-normal" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>Welcome</span>
          <button
            onClick={handleProfileClick}
            className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors"
            style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}
          >
            {getUserDisplayName()}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Branch Selection Dropdown */}
        {userBranches.length > 0 && (
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary-600" />
            <Select 
              value={selectedBranch?.toString() || ""} 
              onValueChange={(value) => setSelectedBranch(value ? parseInt(value) : null)}
            >
              <SelectTrigger className="w-48 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                <SelectValue placeholder="Select Branch" />
              </SelectTrigger>
              <SelectContent>
                {userBranches.map((branch) => (
                  <SelectItem key={branch.branchId} value={branch.branchId.toString()}>
                    {branch.branchCode}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onLogout}
          className="text-red-600 hover:bg-red-50 hover:text-red-700 border border-red-200 hover:border-red-300"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </header>
  );
}